// src/styles.js
import styled, { createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';

export const GlobalStyle = createGlobalStyle`
  * { 
    margin: 0; 
    padding: 0; 
    box-sizing: border-box; 
  }
  
  body { 
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
    overflow-x: hidden; 
    background: #ffffff;
    line-height: 1.6;
  }

  /* Hide Contact Us button on mobile */
  .hide-on-mobile {
    @media (max-width: 768px) {
      display: none !important;
    }
  }

  /* Only show on mobile (hamburger button, mobile menu) */
  .show-on-mobile {
    display: none !important;
    @media (max-width: 768px) {
      display: flex !important;
    }
  }

`;

// Shared values
const cardRadius = '16px';
const buttonRadius = '12px';

export const Container = styled.div`
  width: 100%; 
  min-height: 100vh;
`;

// HEADER
export const Header = styled(motion.header)`
  position: fixed;
  top: 0;
  width: 100%;
  background: white;
  padding: 1rem 5%;
  display: flex;
  align-items: center;
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
`;

export const Logo = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.5px;
`;

export const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    width: auto;         
    margin-left: auto;         
    gap: 0;

    button {
      width: auto;  
      min-width: 120px;   
      padding: 1.2rem 1.5rem;
      font-size: 1.1rem;
      white-space: nowrap;     
      text-align: center;
    }
  }
`;

export const CTAButton = styled(motion.button)`
  background: ${props => props.variant === 'outline' ? 'transparent' : '#001F3D'};
  color: ${props => props.variant === 'outline' ? '#001F3D' : 'white'};
  border: ${props => props.variant === 'outline' ? '2px solid #001F3D' : 'none'};
  padding: 0.9rem 2rem;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: ${buttonRadius};

  @media (max-width: 768px) {
    padding: 0.65rem 1.2rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 2rem;
    font-size: 0.8rem;
  }

  &:hover {
    background: ${props => props.variant === 'outline' ? '#fff5f0' : '#e85a2a'};
    transform: translateY(-2px);
  }
`;

export const CTAButtonTwo = styled(motion.button)`
  background: ${props => props.variant === 'outline' ? 'transparent' : 'white'};
  color: ${props => props.variant === 'outline' ? 'white' : 'white'};
  border: ${props => props.variant === 'outline' ? '2px solid white' : 'none'};
  padding: 0.9rem 2rem;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: ${buttonRadius};

  @media (max-width: 768px) {
    padding: 0.65rem 1.2rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 2rem;
    font-size: 0.8rem;
  }

  &:hover {
    background: ${props => props.variant === 'outline' ? 'none' : '#e85a2a'};
    transform: translateY(-2px);
  }
`;

// HERO
export const Hero = styled.section`
  min-height: 95vh;
  background: linear-gradient(135deg, rgba(0,0,0,0.7), rgba(255,107,53,0.3));
  padding: 8rem 5% 4rem;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

export const VideoBackground = styled.video`
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
`;

export const HeroContent = styled(motion.div)`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  z-index: 1;
`;

export const HeroBox = styled(motion.div)`
  background: rgba(0,0,0,0.85);
  padding: 2.5rem;
  max-width: 600px;
  backdrop-filter: blur(12px);
  border-radius: 20px;

  @media (max-width: 968px) {
    padding: 3rem 2rem;
  }
`;

export const HeroTitle = styled.h1`
  font-size: 3.8rem;
  font-weight: 900;
  line-height: 1.1;
  color: white;
  margin-bottom: 1.5rem;
  text-transform: uppercase;

  @media (max-width: 968px) {
    font-size: 2.8rem;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: #eee;
  margin-bottom: 2.5rem;
  line-height: 1.6;
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  text-align: center;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

// ABOUT SECTION
export const AboutSection = styled.section`
  padding: 6rem 5%;
  background: #0f0f0f;
  overflow-x: clip;
`;

export const AboutContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }

  @media (max-width: 390px) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;

    > * {
      min-width: 0;
      max-width: 100%;
    }
  }
`;

export const AboutText = styled(motion.div)``;

export const AboutTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #FFFFFF;
  line-height: 1.2;
`;

export const AboutDescription = styled.p`
  color: #FAFAFA;
  font-size: 1.1rem;
  line-height: 1.8;
`;

export const MetricsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 390px) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
  }
`;

export const MetricCard = styled(motion.div)`
  background: #1a1a1a;
  padding: 2.5rem 2rem;
  text-align: center;
  border-radius: ${cardRadius};
  transition: all 0.3s ease;
  color: white;

  &:hover {
    background: #001F3D;
    transform: translateY(-8px);
  }

  h3 {
    font-size: 3rem;
    font-weight: 900;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1rem;
    font-weight: 500;
    opacity: 0.9;
  }

  @media (max-width: 390px) {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding: 1.75rem 1rem;

    h3 {
      font-size: 2.25rem;
      overflow-wrap: anywhere;
    }

    p {
      font-size: 0.9rem;
    }

    &:hover {
      transform: none;
    }
  }
`;

// SHARED SECTION STYLES
export const Section = styled.section`
  padding: 6rem 5%;
  background: ${props => props.services ? '#F8F7F4' : 'transparent'};
`;

export const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  color: #FFFFFF;
`;

export const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color: #FFFFFF;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

// SERVICES - 3 COLUMNS MAX + RESPONSIVE
export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-content: start;
  gap: 2.5rem;
  margin-top: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const ServiceCard = styled(motion.div)`
  background: white;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: ${cardRadius};

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.12);
  }
`;

export const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: #001F3D;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  border-radius: 16px;

  svg {
    color: white;
    width: 34px;
    height: 34px;
  }
`;

export const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

export const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.7;
  font-size: 1rem;
`;

// HOW IT WORKS
export const HowItWorksSection = styled.section`padding: 6rem 5%; background: white;`;
export const HowItWorksContainer = styled.div`max-width: 1400px; margin: 0 auto;`;
export const StepsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 3rem; margin-top: 3rem; position: relative;
  &::before { content: ''; position: absolute; top: 50px; left: 10%; right: 10%; height: 2px; background: linear-gradient(to right, #001F3D, #001F3D); z-index: 0;
    @media (max-width: 968px) { display: none; }
  }`;
export const StepCard = styled(motion.div)`text-align: center; position: relative; z-index: 1;`;
export const StepNumber = styled.div`width: 80px; height: 80px; background: linear-gradient(135deg, #001F3D, #001F3D); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1.5rem; font-size: 2rem; font-weight: 900; color: white; box-shadow: 0 10px 30px rgba(255,107,53,0.3);`;
export const StepH3 = styled.h3`font-size: 1.4rem; font-weight: 700; margin-bottom: 1rem; color: #1a1a1a;`;
export const StepDescription = styled.p`color: #666; line-height: 1.7; font-size: 1rem;`;

// CTA
export const CTASection = styled.section`padding: 6rem 5%; background: #0f0f0f; text-align: center;`;
export const CTAContent = styled(motion.div)`max-width: 800px; margin: 0 auto;`;
export const CTATitle = styled.h2`font-size: 3rem; font-weight: 800; color: white; margin-bottom: 1.5rem;`;
export const CTAText = styled.p`font-size: 1.2rem; color: #ddd; margin-bottom: 2.5rem; line-height: 1.6;`;
export const CTAButtonLarge = styled(motion.button)`background: #001F3D; color: white; border: none; padding: 1.2rem 3rem; text-decoration: none; font-weight: 700; font-size: 1.1rem; cursor: pointer; transition: all 0.3s ease; border-radius: ${buttonRadius};
  &:hover { background: #e85a2a; transform: translateY(-2px); }`;

// CONTACT
export const ContactSection = styled.section`padding: 6rem 5%; background: #f8f9fa;`;
export const ContactContainer = styled.div`max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; align-items: center;
  @media (max-width: 968px) { grid-template-columns: 1fr; }`;
export const ContactImage = styled.img`width: 100%; height: 100%; object-fit: cover; min-height: 500px; border-radius: ${cardRadius};`;
export const ContactForm = styled(motion.form)`background: white; padding: 3rem; box-shadow: 0 10px 30px rgba(0,0,0,0.1); border-radius: ${cardRadius};`;
export const FormTitle = styled.h3`font-size: 2rem; font-weight: 800; margin-bottom: 1rem; color: #1a1a1a;`;
export const FormSubtitle = styled.p`color: #666; margin-bottom: 2rem; line-height: 1.6;`;
export const FormGroup = styled.div`margin-bottom: 1.5rem;`;
export const FormLabel = styled.label`display: block; margin-bottom: 0.5rem; font-weight: 600; color: #333;`;
export const FormInput = styled.input`width: 100%; padding: 1rem; border: 2px solid #e0e0e0; border-radius: ${buttonRadius}; font-size: 1rem; transition: border-color 0.3s ease;
  &:focus { outline: none; border-color: #001F3D; }`;
export const FormTextarea = styled.textarea`width: 100%; padding: 1rem; border: 2px solid #e0e0e0; border-radius: ${buttonRadius}; font-size: 1rem; min-height: 140px; resize: vertical; font-family: inherit;
  &:focus { outline: none; border-color: #001F3D; }`;
export const SubmitButton = styled(motion.button)`width: 100%; background: #001F3D; color: white; border: none; padding: 1rem 2rem; font-weight: 600; font-size: 1rem; cursor: pointer; transition: all 0.3s ease; border-radius: ${buttonRadius};
  &:hover { background: #001F3D; }`;

// FOOTER
export const Footer = styled.footer`background: #0a0a0a; color: white; padding: 4rem 5% 2rem;`;
export const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  justify-content: space-between;
  text-align: left;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;
export const FooterColumn = styled.div`
  h4 { 
    margin-bottom: 1.5rem; 
    font-size: 1.3rem; 
    font-weight: 700; 
  }
  p, a { 
    color: #ccc; 
    text-decoration: none; 
    display: block; 
    margin-bottom: 0.8rem; 
    transition: color 0.3s ease; 
  }
  a:hover { 
    color: #001F3D; 
  }

  @media (max-width: 768px) {
    text-align: center;
    p { display: inline-block; }
  }
`;
export const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid #333;
  color: #888;
  font-size: 0.9rem;

  a {
    color: #001F3D;
    text-decoration: none;
    font-weight: 600;
  }

  a:hover {
    text-decoration: underline;
  }
`;
